import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { connect } from 'react-redux';

import { 
  REMOVE_PROJECT_MEMBER, REMOVE_PENDING_PROJECT_MEMBER, CHANGE_PROJECT_MEMBER_ROLE, CHANGE_PENDING_PROJECT_MEMBER_ROLE, SiteboardApiValidator,
  userCanAddProjectAdmin, userCanAddProjectMember, userCanAddProjectViewer, userCanRemoveProjectAdmin, userCanRemoveProjectMember, userCanRemoveProjectViewer,
} from '../actions/types';
import { siteboardAction } from '../actions/defaultActions';

import { Trash, } from 'lucide-react';
import ExpandDownIcon from '../icons/expandDownIcon';

const MemberRolePicker = ({ _userId, _projects, _subscriptions, siteboardAction, projectId, memberId, pendingMemberEmail, onChange }) => {

  const DUMMY_MODE = 'DUMMY_MODE';
  const MEMBER_MODE = 'MEMBER_MODE';
  const PENDING_MODE = 'PENDING_MODE';

  const [dummyRole, setDummyRole] = useState('Member');
  const [popupShowing, setPopupShowing] = useState(false);
  const [popupPosition, setPopupPosition] = useState('bottom');

  const containerRef = useRef(null);
  const popupRef = useRef(null);

  // Make popup show over the button if it is too close to the bottom of the screen
  useLayoutEffect(() => {
    if (popupShowing && popupRef.current && containerRef.current) {
      const popupRect = popupRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      //if (containerRect.top - popupRect.height - 8 < 0) {
      if (containerRect.top < window.innerHeight/2) {
        setPopupPosition('bottom');
      } else {
        setPopupPosition('top');
      }
    }
  }, [popupShowing]);

  // Close dropdown if mouse is clicked anywhere outside it, TODO: do this for touch devices too
  useEffect(() => {
    const onMouseDown = (event) => {
     try {
       let element = event.target;
       do {
         if(element === containerRef.current) return;
         element = element.parentNode;
       } while(element.tagName !== 'body' && element.tagName !== 'BODY');
       setPopupShowing(false);
     } catch(error) {}
    };

    window.addEventListener('mousedown', onMouseDown);
    return () => { // remove listener when component unloads
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, []);

  useEffect(() => {
    try { onChange(dummyRole); } catch(error) {}
  }, [dummyRole, onChange]);

  const selectedProject = _projects[projectId];
  const selectedSubscription = selectedProject ? _subscriptions[selectedProject.subscriptionId] : null;

  let mode = DUMMY_MODE;
  let selectedUser = undefined;
  if(selectedProject && memberId in selectedProject.members) {
    selectedUser = selectedProject.members[memberId];
    mode = MEMBER_MODE;
  }
  if(selectedProject && pendingMemberEmail in selectedProject.pendingMembers) {
    selectedUser = selectedProject.pendingMembers[pendingMemberEmail];
    mode = PENDING_MODE;
  }

  const canRemoveMember = (() => {
    if(mode === DUMMY_MODE || !selectedUser) return false;
    if(selectedUser.role === 'Viewer' && userCanRemoveProjectViewer(_userId, selectedSubscription, selectedProject)) return true;
    if(selectedUser.role === 'Member' && userCanRemoveProjectMember(_userId, selectedSubscription, selectedProject)) return true;
    if(selectedUser.role === 'Admin' && userCanRemoveProjectAdmin(_userId, selectedSubscription, selectedProject)) return true;
    return false;
  })();
  const canChangeToAdmin = (() => {
    if(!userCanAddProjectAdmin(_userId, selectedSubscription, selectedProject)) return false;
    // Changing a user's role is equivalent removing them from one role an add them as another, so the requesting user must be able to do both, if changing an existing member's role
    if(mode !== DUMMY_MODE && !canRemoveMember) return false;
    return true;
  })();
  const canChangeToMember = (() => {
    if(!userCanAddProjectMember(_userId, selectedSubscription, selectedProject)) return false;
    if(mode !== DUMMY_MODE && !canRemoveMember) return false;
    return true;
  })();
  const canChangeToViewer = (() => {
    if(!userCanAddProjectViewer(_userId, selectedSubscription, selectedProject)) return false;
    if(mode !== DUMMY_MODE && !canRemoveMember) return false;
    return true;
  })();

  const setRole = (newRole) => {
    if(!canChangeToAdmin && !canChangeToMember && !canChangeToViewer) return;
    if(mode === DUMMY_MODE) {
      setDummyRole(newRole);
    } else if(mode === PENDING_MODE) {
      if(selectedUser && selectedUser.role === newRole) return;
      siteboardAction(CHANGE_PENDING_PROJECT_MEMBER_ROLE, { 
        memberEmail: pendingMemberEmail,
        projectId: projectId,
        subscriptionId: selectedProject.subscriptionId,
        newRole: newRole,
      }, () => {});
    } else if(mode === MEMBER_MODE) {
      if(selectedUser && selectedUser.role === newRole) return;
      siteboardAction(CHANGE_PROJECT_MEMBER_ROLE, { 
        memberId: memberId,
        projectId: projectId,
        subscriptionId: selectedProject.subscriptionId,
        newRole: newRole, 
      }, () => {});
    }
  };

  const removeMember = () => {
    if(!canRemoveMember) return;
    if(mode === PENDING_MODE) {
      siteboardAction(REMOVE_PENDING_PROJECT_MEMBER, {
        removedMemberEmail: pendingMemberEmail,
        projectId: projectId,
        subscriptionId: selectedProject.subscriptionId 
      }, () => {});
    } else if(mode === MEMBER_MODE) {
      siteboardAction(REMOVE_PROJECT_MEMBER, {
        memberId: memberId,
        projectId: projectId,
        subscriptionId: selectedProject.subscriptionId 
      }, () => {});
    }
  };

  let enabled = canChangeToAdmin || canChangeToMember || canChangeToViewer || canRemoveMember;

  return (
    <div 
      ref={containerRef}
      title={enabled ? 'Change member role' : ''}
      className={`
        group/rolepicker relative 
        w-full h-full
        select-none ${enabled ? 'cursor-pointer' : ''}
      `}
      onClick={(event) => {
        if(!enabled) return;
        setPopupShowing(!popupShowing);
      }}
    >
      <div 
        className={`
          flex justify-between items-center 
          bg-neutral-700 text-white w-full h-full 
          rounded-[5px] 
          group-active/rolepicker:scale-[0.97] ${popupPosition === 'top' ? 'origin-bottom-right' : 'origin-top-right'}
          outline outline-1 hover:outline-neutral-950
          ${popupShowing ? 'outline-neutral-950' : 'outline-neutral-600'}
          transition-[outline-color] duration-300
        `}
      >
        <div className={'flex justify-center items-center px-2 w-full h-full text-center grow rounded-[5px] rounded-r-[0px] ' + (popupShowing ? 'bg-neutral-800' : '')}>
          {(mode === DUMMY_MODE ? dummyRole : selectedUser.role)}
        </div>
        {enabled &&
          <div className={'flex items-center h-full group-hover/rolepicker:bg-neutral-800 px-1 rounded-r-[5px] ' + (popupShowing ? ' bg-neutral-800 ' : ' bg-neutral-500 ')}>
            <div className={' ' + (popupShowing ? ' scale-y-[-1]' : ' scale-y-[1]')}>
              <ExpandDownIcon />
            </div>
          </div>
        }
      </div>
      {(popupShowing) &&
        <div 
          ref={popupRef}
          className={`
            absolute right-0 z-[1] ${popupPosition === 'top' ? 'bottom-full -translate-y-[5px]' : 'top-full translate-y-[1px]'}
            flex flex-col 
            bg-neutral-700 rounded-t-[5px] shadow-lg shadow-black 
            w-[370px] max-w-[70vw] py-1 
            group-active/rolepicker:scale-[0.97] ${popupPosition === 'top' ? 'origin-bottom-right' : 'origin-top-right'}
          `}
        >
          {/*<button 
            className={`flex gap-x-2 py-0.5 text-center grow last:rounded-b-[5px] px-4 py-3 ${!canChangeToViewer ? 'text-gray-400' : ' hover:bg-neutral-800 '}`}
            title='Change role to Viewer'
            disabled={!canChangeToViewer}
            onClick={(event) => { setRole('Viewer'); }}
          >
            <div className={'px-1 ' + ((mode === DUMMY_MODE ? dummyRole : selectedUser.role) === 'Viewer' ? '' : 'opacity-0')}>&#10004;</div>
            <div className='flex flex-col justify-start items-start'>
              <div className='grow text-left font-bold'>Viewer</div>
              <div className='text-sm text-left'>Can view project schedules but not create or edit them.</div>
            </div>
          </button>*/}
          {(mode === MEMBER_MODE || mode === PENDING_MODE) && popupPosition !== 'bottom' &&
            <>
              <button 
                className={'flex items-center gap-x-2.5 px-4 py-3 text-center grow first:rounded-t-[5px] ' + (!canRemoveMember ? 'text-gray-400' : ' hover:bg-neutral-800 ')}
                disabled={!canRemoveMember}
                onClick={(event) => { removeMember(); }}
              >
                <Trash size='16' />
                <p className='whitespace-nowrap'>Remove from project</p>
              </button>
              <div className='w-full h-[1px] bg-white/50 my-1'></div>
            </>
          }
          <button 
            className={`flex gap-x-2 py-0.5 text-center grow last:rounded-b-[5px] px-4 py-3 ${!canChangeToMember ? 'text-gray-400' : ' hover:bg-neutral-800 '}`}
            title='Change role to Member'
            disabled={!canChangeToMember}
            onClick={(event) => { setRole('Member'); }}
          >
            <div className={'px-1 ' + ((mode === DUMMY_MODE ? dummyRole : selectedUser.role) === 'Member' ? '' : 'opacity-0')}>&#10004;</div>
            <div className='flex flex-col justify-start items-start'>
              <div className='grow text-left font-bold'>Member</div>
              <div className='text-sm text-left'>Can create, edit, and publish project schedules.</div>
            </div>
          </button>
          <button 
            className={`flex gap-x-2 py-0.5 text-center grow last:rounded-b-[5px] px-4 py-3 ${!canChangeToAdmin ? 'text-gray-400' : ' hover:bg-neutral-800 '}`}
            title='Change role to Admin'
            disabled={!canChangeToAdmin}
            onClick={(event) => { setRole('Admin'); }}
          >
            <div className={'px-1 ' + ((mode === DUMMY_MODE ? dummyRole : selectedUser.role) === 'Admin' ? '' : 'opacity-0')}>&#10004;</div>
            <div className='flex flex-col justify-start items-start'>
              <div className='grow text-left font-bold'>Admin</div>
              <div className='text-sm text-left'>Can add project members and edit project settings.</div>
            </div>
          </button>
          {(mode === MEMBER_MODE || mode === PENDING_MODE) && popupPosition === 'bottom' &&
            <>
              <div className='w-full h-[1px] bg-white/50 my-1'></div>
              <button 
                className={'flex items-center gap-x-2.5 px-4 py-3 text-center grow first:rounded-t-[5px] ' + (!canRemoveMember ? 'text-gray-400' : ' hover:bg-neutral-800 ')}
                disabled={!canRemoveMember}
                onClick={(event) => { removeMember(); }}
              >
                <Trash size='16' />
                <p className='whitespace-nowrap'>Remove from project</p>
              </button>
            </>
          }
        </div>
      }
    </div>
  );
};

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _projects: state.serverSynced.projects,
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, {siteboardAction})(MemberRolePicker);
